import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Typography, Card, Dropdown, Alert, Checkbox, Space, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, RollbackOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import circuitsService, { type Circuit, type CreateCircuitRequest, type UpdateCircuitRequest } from '../../api/circuitsService';
import { toCountryCode } from '../../utils/countryCode';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

/** Format update_datetime - handles BigQuery format { value: string } or plain ISO string. Shows UTC, 24h. */
function formatUpdateDatetime(val: string | { value?: string } | null | undefined): string {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return '-';
  const date = new Date(str);
  return isNaN(date.getTime()) ? '-' : `${date.toLocaleString(undefined, { timeZone: 'UTC', hour12: false })} UTC`;
}

/** Extract timestamp for sorting - handles BigQuery format */
function getTimestampForSort(val: string | { value?: string } | null | undefined): number {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return 0;
  const date = new Date(str);
  return isNaN(date.getTime()) ? 0 : date.getTime();
}

interface CircuitFormValues {
  circuit_id: string;
  display_name: string;
  country: string;
  time_zone: string;
  location_name: string;
  latitude?: number;
  longitude?: number;
}

export const CircuitsManagement = () => {
  const { authState } = useAuth();
  const token = authState.token;
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCircuit, setEditingCircuit] = useState<Circuit | null>(null);
  const [form] = Form.useForm<CircuitFormValues>();

  // Include soft-deleted circuits in the list (for management UI)
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Fetch all circuits
  const fetchCircuits = useCallback(async () => {
    setLoading(true);
    try {
      const data = await circuitsService.getAllCircuits(includeDeleted);
      setCircuits(data);
    } catch (error) {
      message.error('Failed to fetch circuits');
      console.error('Error fetching circuits:', error);
    } finally {
      setLoading(false);
    }
  }, [includeDeleted]);

  useEffect(() => {
    fetchCircuits();
  }, [fetchCircuits]);

  // Handle create/edit modal (don't allow editing deleted circuits)
  const handleOpenModal = (circuit?: Circuit) => {
    if (circuit?.deleted) return;
    if (circuit) {
      setEditingCircuit(circuit);
      form.setFieldsValue(circuit);
    } else {
      setEditingCircuit(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCircuit(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const country = toCountryCode(values.country);

      if (!token) {
        message.error('Authentication required');
        return;
      }

      if (editingCircuit) {
        // Update existing circuit
        const updates: UpdateCircuitRequest = {
          display_name: values.display_name,
          country,
          time_zone: values.time_zone,
          location_name: values.location_name,
          latitude: values.latitude,
          longitude: values.longitude,
        };

        await circuitsService.updateCircuit(editingCircuit.circuit_id, updates, token);
        message.success('Circuit updated successfully');
      } else {
        // Create new circuit
        const circuitData: CreateCircuitRequest = {
          circuit_id: values.circuit_id,
          display_name: values.display_name,
          country,
          time_zone: values.time_zone,
          location_name: values.location_name,
          latitude: values.latitude,
          longitude: values.longitude,
        };

        await circuitsService.createCircuit(circuitData, token);
        message.success('Circuit created successfully');
      }

      handleCloseModal();
      fetchCircuits();
    } catch (error) {
      console.error('Error saving circuit:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save circuit';
      message.error(errorMessage);
    }
  };

  const handleDelete = async (circuitId: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await circuitsService.deleteCircuit(circuitId, token);
      message.success('Circuit soft-deleted successfully');
      fetchCircuits();
    } catch (error) {
      message.error('Failed to delete circuit');
      console.error('Error deleting circuit:', error);
    }
  };

  const handleRestore = async (circuitId: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await circuitsService.setCircuitDeleted(circuitId, false, token);
      message.success('Circuit restored successfully');
      fetchCircuits();
    } catch (error) {
      message.error('Failed to restore circuit');
      console.error('Error restoring circuit:', error);
    }
  };

  const columns: ColumnsType<Circuit> = [
    {
      title: 'Circuit ID',
      dataIndex: 'circuit_id',
      key: 'circuit_id',
      sorter: (a, b) => a.circuit_id.localeCompare(b.circuit_id),
      render: (id: string, record) => (
        <Space size={4}>
          <Text code style={{ fontSize: 12 }}>{id}</Text>
          {record.deleted && <Tag color="red">Deleted</Tag>}
        </Space>
      ),
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      render: (name: string) => (
        <Text style={{ fontSize: 12 }}>{name}</Text>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location_name',
      key: 'location_name',
      sorter: (a, b) => a.location_name.localeCompare(b.location_name),
      render: (name: string) => (
        <Text style={{ fontSize: 12 }}>{name}</Text>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 120,
      sorter: (a, b) => a.country.localeCompare(b.country),
      render: (country: string) => (
        <Text style={{ fontSize: 12 }}>{country}</Text>
      ),
    },
    {
      title: 'Time Zone',
      dataIndex: 'time_zone',
      key: 'time_zone',
      render: (tz: string) => (
        <Text style={{ fontSize: 12 }}>{tz}</Text>
      ),
    },
    {
      title: 'Latitude',
      dataIndex: 'latitude',
      key: 'latitude',
      width: 100,
      render: (value) => (
        <Text style={{ fontSize: 12 }}>{value?.toFixed(4) || '-'}</Text>
      ),
    },
    {
      title: 'Longitude',
      dataIndex: 'longitude',
      key: 'longitude',
      width: 100,
      render: (value) => (
        <Text style={{ fontSize: 12 }}>{value?.toFixed(4) || '-'}</Text>
      ),
    },
    {
      title: 'Updated',
      key: 'update_info',
      width: 180,
      sorter: (a, b) => getTimestampForSort(a.update_datetime) - getTimestampForSort(b.update_datetime),
      defaultSortOrder: 'descend',
      render: (_, record) => (
        <Space orientation="vertical" size={0} style={{ fontSize: 11 }}>
          {record.updated_by && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.updated_by}</Text>
          )}
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formatUpdateDatetime(record.update_datetime)}
          </Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          ...(record.deleted
            ? [
                {
                  key: 'restore',
                  icon: <RollbackOutlined />,
                  label: 'Restore',
                  onClick: () => handleRestore(record.circuit_id),
                },
              ]
            : [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Edit',
                  onClick: () => handleOpenModal(record),
                },
                { type: 'divider' as const },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Delete',
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Soft-delete Circuit',
                      content: (
                        <div>
                          Are you sure you want to soft-delete <Text code>{record.circuit_id}</Text>?
                          <br />
                          <Text type="secondary">The circuit can be restored later from the list.</Text>
                        </div>
                      ),
                      okText: 'Yes, Delete',
                      cancelText: 'Cancel',
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record.circuit_id),
                    });
                  },
                },
              ]),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Title level={2} style={{ margin: 0 }}>Circuits Management</Title>
          <Space>
            <Checkbox
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            >
              Include deleted
            </Checkbox>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            Add Circuit
          </Button>
          </Space>
        </div>

        <Alert
          title="Circuits Configuration"
          description={
            <span>
              Manage race circuits and their locations. Each circuit defines the venue details,
              time zone for local time display, and geographic coordinates for weather data fetching.
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
        />

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={circuits}
            rowKey="circuit_id"
            loading={loading}
            pagination={false}
            scroll={{ y: 'calc(100vh - 280px)' }}
            size="small"
            footer={() => (
              <div style={{ textAlign: 'right', color: '#8c8c8c', fontSize: 12 }}>
                {circuits.length} circuit{circuits.length !== 1 ? 's' : ''}
              </div>
            )}
          />
        </div>
      </Card>

      {/* Modal */}

      <Modal
        title={editingCircuit ? 'Edit Circuit' : 'Add Circuit'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        width={600}
        okText={editingCircuit ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: '24px' }}
        >
          <Form.Item
            name="circuit_id"
            label="Circuit ID"
            rules={[
              { required: true, message: 'Please enter circuit ID' },
              { pattern: /^[a-z0-9_-]+$/, message: 'Only lowercase letters, numbers, hyphens and underscores allowed' },
            ]}
          >
            <Input placeholder="e.g., monaco, silverstone" disabled={!!editingCircuit} />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="Display Name"
            rules={[{ required: true, message: 'Please enter display name' }]}
          >
            <Input placeholder="e.g., Monaco Grand Prix Circuit" />
          </Form.Item>

          <Form.Item
            name="location_name"
            label="Location Name"
            rules={[{ required: true, message: 'Please enter location name' }]}
          >
            <Input placeholder="e.g., Monte Carlo" />
          </Form.Item>

          <Form.Item
            name="country"
            label="Country"
            extra="ISO country code (e.g. ES) or country name (e.g. Spain). Stored as a 2-letter code for flags."
            rules={[
              { required: true, message: 'Please enter country' },
              {
                validator: (_, value) => {
                  const code = toCountryCode(value);
                  if (code && /^[A-Z]{2}$/.test(code)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Enter a 2-letter ISO code (ES) or a recognized country name (Spain)'));
                },
              },
            ]}
          >
            <Input placeholder="e.g., ES or Spain" />
          </Form.Item>

          <Form.Item
            name="time_zone"
            label="Time Zone"
            rules={[{ required: true, message: 'Please enter time zone' }]}
          >
            <Input placeholder="e.g., Europe/Monaco" />
          </Form.Item>

          <Form.Item
            name="latitude"
            label="Latitude"
            rules={[
              { type: 'number', min: -90, max: 90, message: 'Latitude must be between -90 and 90' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="e.g., 43.7347"
              step={0.0001}
              precision={4}
            />
          </Form.Item>

          <Form.Item
            name="longitude"
            label="Longitude"
            rules={[
              { type: 'number', min: -180, max: 180, message: 'Longitude must be between -180 and 180' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="e.g., 7.4206"
              step={0.0001}
              precision={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CircuitsManagement;

